import { ButtonVariant } from "@amzn/stencil-react-components/button";
import { FormWrapper } from "@amzn/stencil-react-components/form";
import { InputWrapper } from "@amzn/stencil-react-components/input";
import { Col, GridContainer, GridItem, Row } from "@amzn/stencil-react-components/layout";
import { Select } from "@amzn/stencil-react-components/select";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { Button } from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import { Input } from "@amzn/uxm-stencil-react-components/lib/src/stencil/input";
import { Paths } from "common/paths/paths";
import { getString } from "common/uistringlabels/uiStringUtils";
import { useAIForMeData } from "hooks/useAIForMeData";
import { UserPermissions } from "lib/services/user-permissions";
import React, { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

import {
  AiForMeFormData,
  ASR_TALOS_COMBINED_PATTERN,
  BINDLE_PATTERN,
  SecurityReviewSystem,
  TOOL_MATURITY_OPTIONS,
  ToolMaturity,
  YES_NO_OPTIONS,
  YesNo,
} from "./constants/platform-constants";
import { detectLinkType } from "./helpers/custom-built-helper";
import { getPublicDataClassificationValue } from "./helpers/data-classification-helper";
import { MultiSelect } from "./MultiSelect";

const createSecurityReviewLinkFooter = (securityReviewSystem: SecurityReviewSystem) => {
  const SecurityReviewLinkFooter = () => {
    if (securityReviewSystem !== SecurityReviewSystem.OTHER) {
      return <Text fontSize="T100">Detected as {securityReviewSystem} link</Text>;
    }
    return (
      <Col rowGap="4px">
        <Text fontSize="T100">
          Talos: https://talos.security.aws.a2z.com/#/talos/engagement/arn:aws:talos-engagement:engagement/
          {"{engagement-id}"}/
        </Text>
        <Text fontSize="T100">ASR: https://asr.security.amazon.dev/applications/{"{app-id}"}/details</Text>
      </Col>
    );
  };

  SecurityReviewLinkFooter.displayName = "SecurityReviewLinkFooter";
  return SecurityReviewLinkFooter;
};

export interface AiForMeCustomBuiltDetailsProps {
  formData: AiForMeFormData;
  onNext: (data: Partial<AiForMeFormData>) => void;
  onBack: (data?: Partial<AiForMeFormData>) => void;
}

export const AiForMeCustomBuiltDetails: FC<AiForMeCustomBuiltDetailsProps> = ({ formData, onNext, onBack }) => {
  const history = useHistory();

  const { dataClassifications, availableModels, loadingAIForMe } = useAIForMeData();
  const defaultPublicClassification = useMemo(
    () => getPublicDataClassificationValue(dataClassifications || []),
    [dataClassifications]
  );

  const [securityReviewed, setSecurityReviewed] = useState<YesNo>(YesNo.NO);
  const [toolMaturity, setToolMaturity] = useState<ToolMaturity>(ToolMaturity.DEVELOPMENT);
  const [dataClassification, setDataClassification] = useState<string>(defaultPublicClassification);
  const [securityReviewLink, setSecurityReviewLink] = useState<string>("");
  const [securityReviewSystem, setSecurityReviewSystem] = useState<SecurityReviewSystem>(SecurityReviewSystem.OTHER);
  const [modelsUsed, setModelsUsed] = useState<string[]>([]);
  const [otherModel, setOtherModel] = useState<string>("");
  const [storesInputPrompts, setStoresInputPrompts] = useState<YesNo>(YesNo.NO);
  const [collectsDataForRetraining, setCollectsDataForRetraining] = useState<YesNo>(YesNo.NO);
  const [username, setUsername] = useState<string>("");
  const [toolBindle, setToolBindle] = useState<string>("");
  const [toolCTILink, setToolCTILink] = useState<string>("");
  const [modelsValidationError, setModelsValidationError] = useState<string>("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // pre populate form data if availiable on component render (i.e. from back button on review page)
  useEffect(() => {
    if (formData) {
      const secReviewed = (formData.securityReviewed as YesNo) || YesNo.NO;
      setSecurityReviewed(secReviewed);

      // set tool maturity to prod if security review is Yes and no explicit maturity has been set
      if (secReviewed === YesNo.YES && !formData.toolMaturity) {
        setToolMaturity(ToolMaturity.PRODUCTION);
      } else {
        setToolMaturity(formData.toolMaturity || ToolMaturity.DEVELOPMENT);
      }

      setDataClassification(formData.dataClassification || defaultPublicClassification);
      setSecurityReviewLink(formData.securityReviewLink || "");

      if (formData.securityReviewLink && !formData.securityReviewSystem) {
        setSecurityReviewSystem(detectLinkType(formData.securityReviewLink.trim()));
      } else {
        setSecurityReviewSystem(formData.securityReviewSystem || SecurityReviewSystem.OTHER);
      }

      setModelsUsed(formData.modelsUsed || []);
      setOtherModel(formData.otherModel || "");
      setStoresInputPrompts((formData.storesInputPrompts as YesNo) || YesNo.NO);
      setCollectsDataForRetraining((formData.collectsDataForRetraining as YesNo) || YesNo.NO);
      setUsername(formData.username || UserPermissions.current.username || "");
      setToolBindle(formData.toolBindle || "");
      setToolCTILink(formData.toolCTILink || "");
    }
  }, [formData]);

  // reset the form fields when user changes sec review status, only certain fields should be shown depending on sec. status
  const handleSecurityReviewChange = (value: string) => {
    const newValue = value as YesNo;

    // prompting user to confirm that changing sec status will reset fields
    const hasDataToLose =
      securityReviewLink ||
      modelsUsed.length > 0 ||
      otherModel ||
      storesInputPrompts !== YesNo.NO ||
      collectsDataForRetraining !== YesNo.NO ||
      toolBindle ||
      toolCTILink;

    if (hasDataToLose && securityReviewed !== newValue) {
      if (!window.confirm("Changing security review status will reset form fields on this page. Continue?")) {
        return;
      }
    }

    setSecurityReviewed(newValue);
    setSecurityReviewLink("");
    setSecurityReviewSystem(SecurityReviewSystem.OTHER);
    setModelsUsed([]);
    setOtherModel("");
    setStoresInputPrompts(YesNo.NO);
    setCollectsDataForRetraining(YesNo.NO);
    setUsername(UserPermissions.current.username || "");
    setToolBindle("");

    // if new sec review is yes, set the tool maturity to prod.
    if (newValue === YesNo.YES) {
      setToolMaturity(ToolMaturity.PRODUCTION);
    } else {
      setToolMaturity(ToolMaturity.DEVELOPMENT);
    }

    setDataClassification(defaultPublicClassification);
    setToolCTILink("");
  };

  const handleSecurityReviewLinkChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value;
    const trimmedUrl = url.trim();
    setSecurityReviewLink(url);
    setSecurityReviewSystem(detectLinkType(trimmedUrl));
  }, []);

  const handleBackClick = () => {
    // Collect current form data to pass back, following same pattern as handleSubmit
    const currentData = {
      ...formData,
      securityReviewed,
      toolMaturity,
      dataClassification,
      securityReviewLink,
      securityReviewSystem,
      modelsUsed,
      otherModel,
      storesInputPrompts,
      collectsDataForRetraining,
      username,
      toolBindle,
      toolCTILink,
    };
    onBack(currentData);
  };

  // we don't allow users to change data classification if these conditions are met
  const isDataClassificationDisabled = (): boolean => {
    return (
      securityReviewed === YesNo.NO || toolMaturity === ToolMaturity.DEVELOPMENT || toolMaturity === ToolMaturity.BETA
    );
  };

  const getDataClassificationFooter = (): string => {
    if (loadingAIForMe) {
      return "";
    }

    if (securityReviewed === YesNo.YES && toolMaturity === ToolMaturity.PRODUCTION) {
      return "";
    }
    if (securityReviewed === YesNo.NO) {
      return getString("aiForMe.dataClassificationMessages.noSecurityReview", {
        defaultPublicClassification,
      });
    }
    return getString("aiForMe.dataClassificationMessages.nonProductionTool", {
      defaultPublicClassification,
      toolMaturity,
      productionMaturity: ToolMaturity.PRODUCTION,
    });
  };

  const handleToolMaturityChange = useCallback(
    (maturity: ToolMaturity) => {
      setToolMaturity(maturity);

      // If tool has not been security reviewed or its not in prod, reset data classification
      if (securityReviewed === YesNo.NO || maturity === ToolMaturity.DEVELOPMENT || maturity === ToolMaturity.BETA) {
        setDataClassification(defaultPublicClassification);
      }
    },
    [securityReviewed, defaultPublicClassification]
  );

  const handleModelsChange = useCallback((selectedModels: string[]) => {
    setModelsUsed(selectedModels);
    // Clear validation error when user selects models
    if (selectedModels.length > 0) {
      setModelsValidationError("");
    }
    // Clear other model field when "Other" is deselected
    if (!selectedModels.includes("Other")) {
      setOtherModel("");
    }
  }, []);

  // We should ask for additional info from users only under these conditions
  const shouldShowCommonSection = (): boolean => {
    return (
      securityReviewed === YesNo.NO ||
      (securityReviewed === YesNo.YES && securityReviewSystem === SecurityReviewSystem.TALOS)
    );
  };

  const handleSubmit = (e: React.FormEvent<HTMLElement>) => {
    e.preventDefault();

    // Validate that at least one model is selected when common section is shown
    if (shouldShowCommonSection() && modelsUsed.length === 0) {
      setModelsValidationError("Please select at least one AI model.");
      const modelsSelect = document.querySelector('[data-cy="models-select"]');
      if (modelsSelect) {
        modelsSelect.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setModelsValidationError("");

    const updatedData = {
      ...formData,
      securityReviewed,
      toolMaturity,
      dataClassification,
      securityReviewLink,
      securityReviewSystem,
      modelsUsed,
      otherModel,
      storesInputPrompts,
      collectsDataForRetraining,
      username,
      toolBindle,
      toolCTILink,
    };

    onNext(updatedData);
  };

  return (
    <GridContainer>
      <GridItem lg={3} md={2}>
        &nbsp;
      </GridItem>
      <GridItem lg={6} md={4}>
        <form onSubmit={handleSubmit} style={{ width: "100%" }}>
          <FormWrapper width={"100%"}>
            <Col rowGap={"30px"}>
              <Text fontSize="T400" fontWeight="bold" data-cy="custom-built-details-title">
                Custom Built Tool Details
              </Text>
              <Text fontSize="T100" data-cy="custom-built-details-subtitle">
                Please provide additional information about your custom built tool.
              </Text>

              <InputWrapper labelText={getString("aiForMe.formSecurityReview")} id="security-review">
                {(inputProps) => (
                  <Select
                    options={YES_NO_OPTIONS}
                    value={securityReviewed}
                    onChange={handleSecurityReviewChange}
                    data-cy="security-review-select"
                    defaultValue={YesNo.NO}
                    {...inputProps}
                  />
                )}
              </InputWrapper>

              <InputWrapper labelText={getString("aiForMe.formToolMaturity")} id="tool-maturity">
                {(inputProps) => (
                  <Select
                    options={TOOL_MATURITY_OPTIONS}
                    value={toolMaturity}
                    onChange={(value: string) => handleToolMaturityChange(value as ToolMaturity)}
                    data-cy="custom-built-tool-maturity"
                    defaultValue={ToolMaturity.DEVELOPMENT}
                    {...inputProps}
                  />
                )}
              </InputWrapper>

              <InputWrapper
                labelText={getString("aiForMe.formDataClassification")}
                id="data-classification"
                footer={getDataClassificationFooter()}
              >
                {loadingAIForMe
                  ? () => <Spinner size={SpinnerSize.Small} />
                  : (inputProps) => (
                      <Select
                        options={dataClassifications}
                        value={dataClassification}
                        onChange={(value: string) => setDataClassification(value)}
                        disabled={isDataClassificationDisabled()}
                        data-cy="custom-built-data-classification"
                        {...inputProps}
                      />
                    )}
              </InputWrapper>

              {securityReviewed === YesNo.YES && (
                <InputWrapper
                  labelText={getString("aiForMe.formAsrTalosLink")}
                  id="security-review-link"
                  footer={createSecurityReviewLinkFooter(securityReviewSystem)}
                >
                  {(inputProps) => (
                    <Input
                      placeholder="Enter ASR or Talos link"
                      type="url"
                      required
                      pattern={ASR_TALOS_COMBINED_PATTERN}
                      value={securityReviewLink}
                      onChange={handleSecurityReviewLinkChange}
                      data-cy="security-review-link"
                      touchpoint="security-review-link-input"
                      {...inputProps}
                    />
                  )}
                </InputWrapper>
              )}

              {/* Common Section - Show for: No Security Review OR (Yes Security Review AND Talos Link) */}
              {shouldShowCommonSection() && (
                <>
                  <InputWrapper
                    labelText={getString("aiForMe.formAvailableModels")}
                    id="models-select"
                    footer={modelsValidationError}
                    error={!!modelsValidationError}
                  >
                    {(inputProps) => (
                      <MultiSelect
                        options={availableModels}
                        value={modelsUsed}
                        onChange={handleModelsChange}
                        columns={3}
                        isLoading={loadingAIForMe}
                        data-cy="models-select"
                        touchpoint="custom-built-models-multiselect"
                        {...inputProps}
                      />
                    )}
                  </InputWrapper>

                  {/* Other Model Name - show if "Other" is selected in availiable models */}
                  {modelsUsed.includes("Other") && (
                    <InputWrapper labelText={getString("aiForMe.formOtherModelName")} id="other-model-name">
                      {(inputProps) => (
                        <Input
                          placeholder="Enter the model name"
                          required
                          value={otherModel}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOtherModel(e.target.value)}
                          data-cy="other-model-name"
                          touchpoint="other-model-name-input"
                          {...inputProps}
                        />
                      )}
                    </InputWrapper>
                  )}

                  <InputWrapper labelText={getString("aiForMe.formStoreInputPrompts")} id="store-prompts">
                    {(inputProps) => (
                      <Select
                        options={YES_NO_OPTIONS}
                        value={storesInputPrompts}
                        onChange={(value: string) => setStoresInputPrompts(value as YesNo)}
                        data-cy="store-prompts-select"
                        defaultValue={YesNo.NO}
                        {...inputProps}
                      />
                    )}
                  </InputWrapper>

                  <InputWrapper labelText={getString("aiForMe.formUseDataForRetraining")} id="data-retraining">
                    {(inputProps) => (
                      <Select
                        options={YES_NO_OPTIONS}
                        value={collectsDataForRetraining}
                        onChange={(value: string) => setCollectsDataForRetraining(value as YesNo)}
                        data-cy="data-retraining-select"
                        defaultValue={YesNo.NO}
                        {...inputProps}
                      />
                    )}
                  </InputWrapper>

                  <InputWrapper labelText={getString("aiForMe.formToolOwnerAlias")} id="tool-owner-alias">
                    {(inputProps) => (
                      <Input
                        placeholder="Enter tool owner alias"
                        required
                        value={username}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setUsername(e.target.value)}
                        data-cy="tool-owner-alias"
                        touchpoint="tool-owner-alias-input"
                        {...inputProps}
                      />
                    )}
                  </InputWrapper>

                  <InputWrapper
                    labelText={getString("aiForMe.formToolBindle")}
                    id="tool-bindle"
                    footer="Enter a valid bindle (e.g. amzn1.bindle.resource.abc123)"
                  >
                    {(inputProps) => (
                      <Input
                        placeholder="Enter tool bindle"
                        required
                        pattern={BINDLE_PATTERN}
                        value={toolBindle}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToolBindle(e.target.value)}
                        data-cy="tool-bindle"
                        touchpoint="tool-bindle-input"
                        {...inputProps}
                      />
                    )}
                  </InputWrapper>

                  <InputWrapper labelText={getString("aiForMe.formCti")} id="cti">
                    {(inputProps) => (
                      <Input
                        placeholder="Enter CTI URL"
                        type="url"
                        value={toolCTILink}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setToolCTILink(e.target.value)}
                        data-cy="cti"
                        touchpoint="cti-input"
                        {...inputProps}
                      />
                    )}
                  </InputWrapper>
                </>
              )}

              <Row justifyContent="space-between" columnGap={5}>
                <Button
                  variant={ButtonVariant.Secondary}
                  onClick={() => history.push(Paths.aiforme.url)}
                  type="button"
                  data-cy="custom-built-cancel-button"
                  touchpoint="custom-built-cancel"
                >
                  {getString("aiForMe.cancel")}
                </Button>
                <Row columnGap={5}>
                  <Button
                    variant={ButtonVariant.Secondary}
                    onClick={handleBackClick}
                    type="button"
                    data-cy="custom-built-back-button"
                    touchpoint="custom-built-back"
                  >
                    {getString("aiForMe.previousPage")}
                  </Button>
                  <Button
                    variant={ButtonVariant.Primary}
                    type="submit"
                    data-cy="custom-built-next-button"
                    touchpoint="custom-built-next"
                  >
                    {getString("aiForMe.nextPage")}
                  </Button>
                </Row>
              </Row>
            </Col>
          </FormWrapper>
        </form>
      </GridItem>
      <GridItem lg={3} md={2}>
        &nbsp;
      </GridItem>
    </GridContainer>
  );
};
